import type { ReactNode } from 'react';

/**
 * Minimal Markdown renderer for KB articles: headings, paragraphs, lists, quotes, bold,
 * italic, code and links. Builds React elements (never innerHTML), so content can't inject markup.
 */
export function Markdown({ source }: { source: string }) {
  return <div className="space-y-3 text-sm leading-relaxed text-slate-700">{blocks(source)}</div>;
}

function blocks(src: string): ReactNode[] {
  const lines = src.replace(/\r\n/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    if (!line.trim()) {
      i++;
      continue;
    }
    const h = /^(#{1,3})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]!.length;
      const cls =
        level === 1 ? 'text-xl font-bold' : level === 2 ? 'pt-2 text-base font-bold' : 'text-sm font-bold';
      const Tag = level === 1 ? 'h2' : level === 2 ? 'h3' : 'h4';
      out.push(
        <Tag key={i} className={cls}>
          {inline(h[2]!)}
        </Tag>,
      );
      i++;
      continue;
    }
    if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
      const ordered = /^\s*\d+\./.test(line);
      const items: string[] = [];
      while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i]!)) {
        items.push(lines[i]!.replace(/^\s*([-*]|\d+\.)\s+/, ''));
        i++;
      }
      const List = ordered ? 'ol' : 'ul';
      out.push(
        <List key={i} className={ordered ? 'list-decimal space-y-1 pl-5' : 'list-disc space-y-1 pl-5'}>
          {items.map((it, j) => (
            <li key={j}>{inline(it)}</li>
          ))}
        </List>,
      );
      continue;
    }
    if (line.startsWith('>')) {
      const quote: string[] = [];
      while (i < lines.length && lines[i]!.startsWith('>')) quote.push(lines[i++]!.replace(/^>\s?/, ''));
      out.push(
        <blockquote
          key={i}
          className="border-brand-300 bg-brand-50/50 rounded-r-lg border-l-4 px-3 py-2 text-slate-700"
        >
          {inline(quote.join(' '))}
        </blockquote>,
      );
      continue;
    }
    const para: string[] = [];
    while (i < lines.length && lines[i]!.trim() && !/^(#{1,3}\s|>|\s*([-*]|\d+\.)\s)/.test(lines[i]!))
      para.push(lines[i++]!);
    out.push(<p key={i}>{inline(para.join(' '))}</p>);
  }
  return out;
}

const TOKEN = /(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\)|\*[^*]+\*|_[^_]+_)/g;

function inline(text: string): ReactNode[] {
  return text.split(TOKEN).map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part))
      return (
        <strong key={i} className="font-semibold text-slate-900">
          {part.slice(2, -2)}
        </strong>
      );
    if (/^`[^`]+`$/.test(part))
      return (
        <code key={i} className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em]">
          {part.slice(1, -1)}
        </code>
      );
    if (/^(\*[^*]+\*|_[^_]+_)$/.test(part)) return <em key={i}>{part.slice(1, -1)}</em>;
    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
    if (link) {
      const href = link[2]!;
      return /^(https?:|mailto:)/i.test(href) ? (
        <a key={i} href={href} target="_blank" rel="noreferrer noopener" className="text-brand-700 underline">
          {link[1]}
        </a>
      ) : (
        link[1]
      );
    }
    return part;
  });
}
